/**
 * Generic placeholder page for routes whose real implementation hasn't landed
 * yet. Reads its descriptor from the route registry so each placeholder links
 * to its spec and shows its build phase.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-19-v1
 */
import { useLocation } from 'react-router-dom';
import { Box } from 'lucide-react';
import { findRoute } from '../../lib/routes';
import { Chip } from '../components/chip';

export function PlaceholderPage() {
    const loc = useLocation();
    const route = findRoute(loc.pathname);

    if (!route) {
        return (
            <div className="placeholder-page">
                <Box />
                <div>
                    <h2>Not found</h2>
                    <p>No admin route matches {loc.pathname}.</p>
                </div>
            </div>
        );
    }

    const phaseTone =
        route.phase === 1
            ? 'info'
            : route.phase === 2
              ? 'purple'
              : route.phase === 3
                ? 'warning'
                : 'muted';

    return (
        <div className="stack-16">
            <header className="page-header">
                <div className="page-title-group">
                    <h1>{route.label}</h1>
                    <p>Spec-mapped — UI to follow.</p>
                </div>
                <div className="page-actions">
                    <Chip tone={phaseTone}>Phase {route.phase}</Chip>
                </div>
            </header>

            <div className="placeholder-page">
                <Box />
                <div>
                    <h2>Placeholder page</h2>
                    <p>
                        This route is reserved for the <strong>{route.label}</strong> view. See{' '}
                        <code>docs/specs/{route.spec}</code> for the full specification and
                        acceptance criteria.
                    </p>
                </div>
            </div>
        </div>
    );
}
